// 单元格展示对应的组件类型 (不包括表头单元格)
import { ArtColumn, features } from "ali-react-table";
import React from "react";
import type { BubbleProps } from "tea-component";
import type { INumericalFormatColumnConfig } from '@dbl/core/chartEditor';
// 单元格渲染组件类型
export enum AliTableCellTypeEnum {
  Original = 'original',
  Url = 'url',
  BarPercent = 'barPercent',
  /** 仅数值条件（颜色 + 左/右图标） */
  NumericalCondition = 'numericalCondition',
  /** 条形图 + 数值条件同列叠加 */
  BarPercentNumerical = 'barPercentNumerical',
  Compare = 'compare',
}

// 单元格组件参数
export interface IAliTableCellComponentProps {
  text: string;
  row: any;
  column: TAliTableColumn;
}

// 支持的列数据类型
export type ColumnDataType = 'date' | 'string' | 'number' | 'float' | 'percent' | 'bigint' | 'decimal';

// 占比配置
export type TBarPercentConfig = {
  id: string;
  min: number;
  max: number;
  metric: string;
  table_code: string;
  colorTheme: {
    type: 'gradientColor' | 'solidColor';
    color: {
      hexa: string;
      alpha: string;
    }[];
  };
};

// 后端列信息（用于生成表格列）
export interface ITableColumnInfo {
  code?: string; // 用于透传 crosstab leaf code（leaf.name 可能为空字符串）
  name: string; // 如: t0_date
  name_cn?: string;
  name_en?: string;
  origin_name: string; // 如: date
  can_sort?: boolean;
  type: number; // 0 指标列, 1 维度列
  column_date_type: ColumnDataType; // date | string | number | float | percent
  aggregate?: string;
  accuracy: number;
  unit: string | null;
  attribute?: number;
  table_code?: string; // 如: t0
  table_id?: string;
  table_name?: string;
  customize_name?: string;
  customize_name_en?: string;
  is_compare_date?: boolean; // 是否对比时间列
  is_user_filter?: boolean; // 是否是用户筛选器列
}

// 在ali表格原有column属性基础上增加的 一些需要传参的数据
export type TCustomColumn = {
  code: string;
  // 中英文翻译
  isEn: boolean;
  // 直接传递自己所需的数据, 不是ali-react-table原本的属性
  valueType: ColumnDataType;
  cellType: AliTableCellTypeEnum; // 代表单元格要以哪种形式渲染, 目前是 普通, url
  // 获取表头描述的key
  descKey?: string;
  // 占比配置
  barPercentConfig?: TBarPercentConfig;
  /** 数值条件 rules */
  numericalFormatConfig?: INumericalFormatColumnConfig;
  originName?: string;
  tableCode?: string;
  // 数据展示的小数位数
  accuracy: number;
  // 用bmk还是千分隔符展示
  unit: string;
  // 是否是对比日期单元格渲染
  isCompareCell?: boolean;
  // 是否对比时间列
  isCompareDate?: boolean;
  // 是否是用户筛选器列
  isUserFilter?: boolean;
  // 1维度列 0指标列
  type: 0 | 1;
}

// Datalab中Ali表格需要传递的column字段类型
export type TAliTableColumn = ArtColumn & TCustomColumn;

export type TAliTableRow = {
  [key: string]: any;
}

export type TSortOrder = 'desc' | 'asc' | 'none'

export type TSortItem = { code: string; order: TSortOrder; }

export type TSort = { value: string; is_statistics: boolean; originalIndex?: number; }

// HeaderCellBubble 负责控制 Bubble 的 visible/content/referenceElement 等受控属性
// headerBubbleConfig 只允许透传其余展示/定位相关配置，避免覆盖内部受控逻辑。
type THeaderBubbleConfig = Omit<
  BubbleProps,
  'content' | 'visible' | 'referenceElement' | 'className' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'
>;
// 排序函数类型
export type SortFunction = (a: TSort, b: TSort) => number;

// 分页参数相关
export interface PagingQuery {
  /**
   * 页码：从 1 开始索引
   */
  pageIndex?: number;
  /**
   * 页长：表示每页记录数
   */
  pageSize?: number;
}
export interface PaginationProps extends Partial<PagingQuery> {
  defaultPageSize?: number;
  recordCount?: number; // 内部计算数据总个数
  // 切换分页回调函数
  onPagingChange?: (query: PagingQuery) => void;
  // 切换页长时，是否自动把页码重设为 1
  isPagingReset?: boolean;
  // 页组件显示的说明信息，不传将渲染数据个数信息
  stateText?: React.ReactNode;
  // 支持的页长设置
  pageSizeOptions?: number[];
  // 是否显示状态文案
  stateTextVisible?: boolean;
  // 是否显示页长选择 @default true
  pageSizeVisible?: boolean;
  // 是否显示页码输入
  pageIndexVisible?: boolean;
  // 是否显示切页按钮（上一页/下一页）
  jumpVisible?: boolean;
  // 是否显示第一页和最后一页按钮
  endJumpVisible?: boolean;
  /**
   * 当前页码大于总页数时的自动重置行为
   * 重置类型：不处理 / 回到首页 / 回到可用的末页
   * @default "unset"
   */
  pageCountChangingResetType?: "unset" | "first" | "last";
  className?: string;
}
// 拖拽修改列宽的配置
export interface IColumnResize extends features.ColumnResizeFeatureOptions {
  isUse?: boolean; // 是否开启拖拽修改列宽功能
}

export interface IMergeSameCellsConfig {
  enabled?: boolean;
}

// 定义AliTableAnalytics组件props类型
export interface IAliTableAnalyticsProps {
  dataSource: any[];
  appendUnsortedData?: any[]; // 需要额外向表格增加的 不参与排序的数据, 比如表格底部的统计值
  columns: TAliTableColumn[];
  /** 合并相同单元格（仅维度列、相邻相同值） */
  mergeSameCells?: IMergeSameCellsConfig;
  /** 是否存在交叉表列结构；交叉表不合并 */
  hasCrosstab?: boolean;
  isEn?: boolean;
  primaryKey?: string | ((row: any) => string);
  // 表格头部单元格 气泡弹窗展示内容的映射
  headerCellBubbleMap?: Record<string, (info: any) => React.ReactNode>;
  // 表头气泡透传配置（受控字段由 HeaderCellBubble 内部固定）
  headerBubbleConfig?: THeaderBubbleConfig;
  // 拖拽修改列宽的配置
  columnResize?: IColumnResize;
  // 排序配置
  sortConfig?: features.SortFeatureOptions & {
    /**
     * 是否允许排序（用于上层业务透传/控制表头交互）。
     * ali-react-table 的类型中未声明该字段，但运行时允许透传。
     */
    sortable?: boolean;
  };
  // 分页配置
  pagination?: PaginationProps;
  /**
   * 为 true 时前端不再对行做分页切片（展示当前 dataSource 全量），
   * 仍渲染 Pagination 以展示总数等，具体控件由 pagination 上的可见性 props 控制。
   */
  clientPagingDisabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

// 定义AliTableAnalytics 组件 ref暴露的方法类型
export interface IAliTableAnalyticsRef {
  changeSorts: (newSorts: TSortItem[]) => void;
  applyColumnOrder: (orderCodes: string[]) => void;
  changePageSize: (pageSize: number) => void;
  getPageSize: () => number | undefined;
}

/**
 * 表格分析数据项接口
 * 支持指标和维度两种类型的数据结构
 */
export interface ITableAnalyticsDataItem {
  // 指标值/维度值
  value: any;
  value_cn?: string;
  value_en?: string;
  // 指标值类型/维度值类型
  value_type?: string;
  // 环比值（环比变化率）【(基准时间的指标值-对比时间的指标值)÷对比时间的指标值]*100%;若分母不存在或为空，环比值显示为:-最大值:+999.99%，最小值:-99.99% 保留两位小数】
  dod?: string;
  // 环比变化率
  change?: string;
  // 百分比（用于柱状图百分比展示）
  bar_percent?: string;
  // 额外值名称（统计值名称）
  statistics_name?: string;
  // 是否额外值标识（是否为统计值）
  is_statistics?: boolean;
}
export interface ITableAnalyticsData {
  [key: string]: ITableAnalyticsDataItem;
}

